import Grid2 from '@mui/material/Grid2';
import React from 'react';
import useStyles from './styles';
import { UserBadge } from 'atoms';
import { publicHrefForProfileLink } from '@elevator/socialite';
import { UserProfile, SocialIconLink, Avatar } from '@elevator/ui';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Hidden from '@mui/material/Hidden';
import Typography from '@mui/material/Typography';
import { ROUTES } from 'config/Nav';
import clsx from 'clsx';

/** Public profile header: no edit CTA (viewing another user or logged out). */
export interface ProfileInfoProps {
  profile: UserProfile;
  isElevatorStaff: boolean;
  mobile: boolean;
}

/** Same header when the current user owns this profile — includes Edit. */
export interface ProfileInfoOwnerProps extends ProfileInfoProps {
  onEditProfile: () => void;
}

type ProfileInfoBaseProps = ProfileInfoProps & {
  showEditProfile: boolean;
  showPreferredOwnerBadge: boolean;
  onEditProfile?: () => void;
};

/**
 * Shared layout for {@link ProfileInfo} and {@link ProfileInfoOwner}.
 */
const ProfileInfoBase = ({
  profile,
  isElevatorStaff,
  mobile,
  showEditProfile,
  showPreferredOwnerBadge,
  onEditProfile,
}: ProfileInfoBaseProps) => {
  const { classes } = useStyles();
  const {
    displayName,
    avatar,
    bio,
    socialMedia,
    verified,
    title,
    role,
    preferredUsername,
  } = profile;

  /** Credit type label (e.g. job title); falls back to `role` when API omits `title`. */
  const roleLabel = (title?.typeName ?? role)?.trim() || '';

  const renderProfileImage = () => (
    <Box paddingRight={2}>
      <div className={classes.profileImage}>
        <Avatar
          src={avatar?.sourceUrl}
          srcSet={avatar?.sourceSet}
          username={preferredUsername}
          size="xlarge"
        />
      </div>
    </Box>
  );

  const renderSocialMedia = () => (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={1}
      alignItems="center"
      justifyContent="flex-start"
      className={classes.socialLinks}
    >
      {socialMedia.map(({ type, link }) => (
        <SocialIconLink
          key={`${type}-${link}`}
          type={type === 'twitter' ? 'x' : type}
          href={publicHrefForProfileLink(type, link)}
          variant="medium"
        />
      ))}
    </Stack>
  );

  const renderActionButton = (text: string, onClick: () => void) => {
    const inline = !mobile;
    return (
      <Button
        fullWidth={!inline && mobile}
        variant="contained"
        color="primary"
        size={mobile ? 'small' : 'large'}
        className={classes.button}
        onClick={onClick}
      >
        {text}
      </Button>
    );
  };

  const renderBio = () => (
    <Typography variant="body1" className={classes.bioText}>
      {bio ? bio : '(No Bio)'}
    </Typography>
  );

  const renderProfileInfoRow = () => (
    <Grid2
      container
      className={classes.profileInfoContainer}
      direction="row"
      justifyContent="space-between"
    >
      <Grid2 size={12} sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
        <Stack direction="column" spacing={1} useFlexGap className={classes.metaStack}>
          {showEditProfile && onEditProfile ? (
            <Box className={classes.roleRowWithEdit}>
              {roleLabel ? (
                <Typography variant="subtitle2" className={classes.role}>
                  {roleLabel}
                </Typography>
              ) : null}
              <Box className={classes.editButtonAnchor}>
                {renderActionButton('Edit', onEditProfile)}
              </Box>
            </Box>
          ) : roleLabel ? (
            <Typography variant="subtitle2" className={classes.role}>
              {roleLabel}
            </Typography>
          ) : null}
          <Typography
            display="inline"
            variant="h5"
            className={clsx(
              classes.username,
              preferredUsername.includes(' ') && classes.usernameBreakWordPolicy,
            )}
          >
            {displayName || preferredUsername}
            <div className={classes.badgeGroupContainer}>
              <div className={classes.badgeGroupInnerContainer}>
                {isElevatorStaff && <UserBadge variant="staff" />}
                {showPreferredOwnerBadge && <UserBadge variant="preferred" />}
                {verified && <UserBadge variant="verified" />}
              </div>
            </div>
          </Typography>
          <Typography
            href={ROUTES.USER.to + '/' + preferredUsername}
            component="a"
            variant="subtitle2"
            className={classes.preferredUsername}
          >
            @{preferredUsername}
          </Typography>
          {socialMedia && socialMedia.length > 0 ? renderSocialMedia() : null}
          <Hidden smDown>
            <Box maxWidth="760px">{renderBio()}</Box>
          </Hidden>
        </Stack>
      </Grid2>
    </Grid2>
  );

  return (
    <>
      <Grid2 container direction="row" className={classes.profileHeaderRow}>
        <Grid2 className={classes.profileImageColumn}>{renderProfileImage()}</Grid2>
        <Grid2
          className={classes.profileInfo}
          sx={{ flex: '1 1 0%', minWidth: 0, width: '100%', maxWidth: '100%' }}
        >
          {renderProfileInfoRow()}
        </Grid2>
      </Grid2>
      <Hidden smUp>
        <Box paddingTop={2}>{renderBio()}</Box>
      </Hidden>
    </>
  );
};

/**
 * Default profile header (no edit actions). Used on `/users/[user]` when the viewer is not the owner.
 */
export const ProfileInfo = (props: ProfileInfoProps) => (
  <ProfileInfoBase {...props} showEditProfile={false} showPreferredOwnerBadge={false} />
);

/**
 * Profile header for the signed-in owner on `/users/[username]`. Renders Edit.
 */
export const ProfileInfoOwner = ({ onEditProfile, ...props }: ProfileInfoOwnerProps) => (
  <ProfileInfoBase
    {...props}
    showEditProfile
    showPreferredOwnerBadge={props.profile.preferred}
    onEditProfile={onEditProfile}
  />
);

export default ProfileInfo;
